import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Button,
  Chip,
  Paper,
  Divider,
  IconButton,
  Link
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  Help as HelpIcon,
  VideoLibrary as VideoIcon,
  Description as DocumentIcon,
  ContactSupport as ContactIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  QuestionAnswer as FaqIcon,
  MenuBook as GuideIcon,
  Lightbulb as TipIcon,
  BugReport as BugIcon,
  Feedback as FeedbackIcon,
  School as TutorialIcon,
  Article as ArticleIcon,
  Launch as ExternalIcon,
  WhatsApp as WhatsAppIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';
import { useAuth } from '@contexts/AuthContext';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`help-tabpanel-${index}`}
      aria-labelledby={`help-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

interface FAQItem {
  question: string;
  answer: string;
  category: string;
}

interface GuideItem {
  title: string;
  description: string;
  icon: React.ReactNode;
  link?: string;
}

const Help: React.FC = () => {
  const { user } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedFaq, setExpandedFaq] = useState<string | false>(false);

  // FAQ-uri organizate pe categorii
  const faqs: FAQItem[] = [
    // Generale
    {
      question: 'Cum pot crea un cont in HealthyPaws?',
      answer: 'Pentru a crea un cont, accesati pagina de inregistrare si selectati tipul de cont dorit (Client, Veterinar sau Clinica). Completati formularul cu datele necesare si verificati adresa de email. Dupa activarea contului, va puteti autentifica si utiliza platforma.',
      category: 'generale'
    },
    {
      question: 'Am uitat parola. Cum o pot reseta?',
      answer: 'Accesati pagina de autentificare si faceti click pe "Ai uitat parola?". Introduceti adresa de email asociata contului si veti primi un link de resetare a parolei. Verificati si folderul Spam daca nu primiti emailul.',
      category: 'generale'
    },
    {
      question: 'Cum pot schimba informatiile din profilul meu?',
      answer: 'Accesati meniul principal si selectati "Profil" sau "Setari". Aici puteti edita informatiile personale, adresa de email, numarul de telefon si alte detalii ale contului.',
      category: 'generale'
    },
    
    // Programari
    {
      question: 'Cum programez o consultatie pentru animalul meu?',
      answer: 'Accesati sectiunea "Programari" din meniu, selectati "Programare noua", alegeti clinica veterinara, medicul dorit, data si ora disponibila. Confirmati programarea si veti primi o notificare de confirmare.',
      category: 'programari'
    },
    {
      question: 'Pot anula sau reprograma o consultatie?',
      answer: 'Da, accesati sectiunea "Programari", gasiti programarea dorita si utilizati optiunile "Anuleaza" sau "Reprogrameaza". Va rugam sa anulati cu cel putin 24 de ore inainte pentru a evita taxele de anulare tardiva.',
      category: 'programari'
    },
    {
      question: 'Cum primesc notificari pentru programarile mele?',
      answer: 'Notificarile se trimit automat prin email si in aplicatie. Puteti personaliza preferintele de notificare din sectiunea "Setari" > "Notificari".',
      category: 'programari'
    },
    
    // Animale/Pacienti
    {
      question: 'Cum adaug un animal in cont?',
      answer: 'Accesati "Animalele mele" din meniu, faceti click pe "Adauga animal nou" si completati detaliile (nume, specie, rasa, data nastere, etc.). Puteti adauga si fotografii si informatii medicale.',
      category: 'animale'
    },
    {
      question: 'Pot adauga mai multe animale in cont?',
      answer: 'Da, puteti adauga un numar nelimitat de animale in contul dumneavoastra. Fiecare animal va avea propriul istoric medical si programari separate.',
      category: 'animale'
    },
    {
      question: 'Cum accesez istoricul medical al animalului?',
      answer: 'Selectati animalul dorit din "Animalele mele" si accesati sectiunea "Istoric Medical". Aici veti gasi toate consultatiile, diagnosticele, tratamentele si vaccinarile.',
      category: 'animale'
    },
    
    // Plati
    {
      question: 'Ce metode de plata sunt acceptate?',
      answer: 'Acceptam plati cu cardul bancar (Visa, Mastercard), transfer bancar si plata cash la clinica. Pentru unele servicii este disponibila si plata in rate.',
      category: 'plati'
    },
    {
      question: 'Cum pot solicita o factura?',
      answer: 'Facturile se genereaza automat dupa fiecare plata si pot fi descarcate din sectiunea "Plati" > "Facturi". Puteti selecta factura dorita si utiliza optiunea "Descarca PDF".',
      category: 'plati'
    },
    {
      question: 'Pot obtine rambursari?',
      answer: 'Rambursarile se fac conform politicii fiecarei clinici. Contactati clinica direct sau serviciul nostru de suport pentru a initia o solicitare de rambursare.',
      category: 'plati'
    },
    
    // Pentru Clinici
    {
      question: 'Cum inregistrez o noua clinica?',
      answer: 'Selectati "Inregistrare Clinica" la crearea contului, completati datele clinicii (nume, CUI, adresa, licente) si asteptati aprobarea de la echipa HealthyPaws. Veti fi notificat prin email cand contul este activat.',
      category: 'clinici'
    },
    {
      question: 'Cum adaug medici veterinari in clinica?',
      answer: 'Accesati "Veterinari" din meniul administratorului de clinica, faceti click pe "Adauga veterinar" si completati datele medicului. Medicul va primi un email de invitatie.',
      category: 'clinici'
    },
    {
      question: 'Cum gestionez inventarul clinicii?',
      answer: 'Accesati sectiunea "Inventar" pentru a adauga produse, medicamente si consumabile. Sistemul ofera alerte automate pentru stocurile scazute si rapoarte detaliate.',
      category: 'clinici'
    },
    
    // Securitate
    {
      question: 'Cat de sigure sunt datele mele?',
      answer: 'Utilizam criptare SSL/TLS pentru toate comunicatiile si stocam datele in servere securizate conform GDPR. Datele medicale sunt strict confidentiale si accesibile doar personalului autorizat.',
      category: 'securitate'
    },
    {
      question: 'Cum raportez o problema de securitate?',
      answer: 'Pentru probleme de securitate, contactati imediat echipa noastra la security@healthypaws.ro sau utilizati formularul de contact. Luam foarte in serios securitatea datelor.',
      category: 'securitate'
    }
  ];

  // Ghiduri rapide
  const quickGuides: GuideItem[] = [
    {
      title: 'Primii pasi in HealthyPaws',
      description: 'Aflati cum sa va configurati contul, sa adaugati animale si sa programati prima consultatie',
      icon: <GuideIcon color="primary" />
    },
    {
      title: 'Programarea consultatiilor',
      description: 'Ghid complet pentru programarea, reprogramarea si anularea consultatiilor',
      icon: <ScheduleIcon color="success" />
    },
    {
      title: 'Gestionarea animalelor',
      description: 'Cum sa adaugati si sa gestionati informatiile despre animalele dumneavoastra',
      icon: <TipIcon color="warning" />
    },
    {
      title: 'Istoric medical si documente',
      description: 'Accesati si descarcati istoricul medical complet al animalului',
      icon: <DocumentIcon color="info" />
    },
    {
      title: 'Plati si facturare',
      description: 'Procesarea platilor, descarcarea facturilor si gestionarea metodelor de plata',
      icon: <ArticleIcon color="error" />
    },
    {
      title: 'Notificari si reminder-e',
      description: 'Configurati notificarile pentru programari, vaccinari si tratamente',
      icon: <FaqIcon color="secondary" />
    }
  ];

  // Videoclipuri tutorial
  const videoTutorials = [
    {
      title: 'Crearea contului si configurare initiala',
      duration: '5:30',
      views: '2.4k'
    },
    {
      title: 'Cum sa programati o consultatie',
      duration: '3:45',
      views: '1.8k'
    },
    {
      title: 'Adaugarea animalelor in cont',
      duration: '4:15',
      views: '1.5k'
    },
    {
      title: 'Gestionarea istoricului medical',
      duration: '6:20',
      views: '980'
    },
    {
      title: 'Plati online si facturare',
      duration: '4:50',
      views: '1.2k'
    }
  ];

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleFaqChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedFaq(isExpanded ? panel : false);
  };

  // Filtrare FAQ-uri pe baza cautarii
  const filteredFaqs = faqs.filter(faq =>
    faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
    faq.answer.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Grupare FAQ-uri pe categorii
  const faqsByCategory = filteredFaqs.reduce((acc, faq) => {
    if (!acc[faq.category]) {
      acc[faq.category] = [];
    }
    acc[faq.category].push(faq);
    return acc;
  }, {} as Record<string, FAQItem[]>);

  const categoryNames: Record<string, string> = {
    generale: 'Intrebari Generale',
    programari: 'Programari',
    animale: 'Animale/Pacienti',
    plati: 'Plati si Facturare',
    clinici: 'Pentru Clinici',
    securitate: 'Securitate si Confidentialitate'
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
          <HelpIcon sx={{ mr: 1, verticalAlign: 'middle', fontSize: 32 }} />
          Centru de Ajutor
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Gasiti raspunsuri la intrebarile dumneavoastra si invatati sa utilizati HealthyPaws la maxim
        </Typography>
      </Box>

      {/* Search Bar */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <TextField
            fullWidth
            placeholder="Cautati in intrebarile frecvente..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              )
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'background.paper'
              }
            }}
          />
        </CardContent>
      </Card>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
          <Tab icon={<FaqIcon />} label="FAQ" iconPosition="start" />
          <Tab icon={<GuideIcon />} label="Ghiduri Rapide" iconPosition="start" />
          <Tab icon={<VideoIcon />} label="Video Tutorial" iconPosition="start" />
          <Tab icon={<ContactIcon />} label="Contact Suport" iconPosition="start" />
        </Tabs>
      </Box>

      {/* Tab: FAQ */}
      <TabPanel value={tabValue} index={0}>
        {Object.keys(faqsByCategory).length > 0 ? (
          Object.keys(faqsByCategory).map((category) => (
            <Box key={category} sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                {categoryNames[category]}
              </Typography>
              {faqsByCategory[category].map((faq, index) => (
                <Accordion
                  key={`${category}-${index}`}
                  expanded={expandedFaq === `${category}-${index}`}
                  onChange={handleFaqChange(`${category}-${index}`)}
                  sx={{ mb: 1 }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography sx={{ fontWeight: 500 }}>{faq.question}</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography color="text.secondary">{faq.answer}</Typography>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          ))
        ) : (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <SearchIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              Nu am gasit rezultate pentru cautarea dumneavoastra
            </Typography>
            <Typography variant="body2" color="text.disabled">
              Incercati sa cautati cu alti termeni sau contactati suportul
            </Typography>
          </Paper>
        )}
      </TabPanel>

      {/* Tab: Ghiduri Rapide */}
      <TabPanel value={tabValue} index={1}>
        <Grid container spacing={3}>
          {quickGuides.map((guide, index) => (
            <Grid size={{ xs: 12, md: 6 }} key={index}>
              <Card sx={{ height: '100%', transition: 'all 0.3s', '&:hover': { boxShadow: 6 } }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <Box sx={{ mt: 0.5 }}>{guide.icon}</Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                        {guide.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {guide.description}
                      </Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        endIcon={<ExternalIcon />}
                      >
                        Citeste mai mult
                      </Button>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </TabPanel>

      {/* Tab: Video Tutorial */}
      <TabPanel value={tabValue} index={2}>
        <List>
          {videoTutorials.map((video, index) => (
            <React.Fragment key={index}>
              <ListItem
                sx={{
                  py: 2,
                  px: 3,
                  borderRadius: 1,
                  mb: 1,
                  backgroundColor: 'background.paper',
                  transition: 'all 0.2s',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                    cursor: 'pointer'
                  }
                }}
              >
                <ListItemIcon>
                  <VideoIcon color="error" sx={{ fontSize: 32 }} />
                </ListItemIcon>
                <ListItemText
                  primary={video.title}
                  secondary={
                    <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                      <Chip label={video.duration} size="small" />
                      <Chip label={`${video.views} vizualizari`} size="small" variant="outlined" />
                    </Box>
                  }
                />
                <IconButton>
                  <ExternalIcon />
                </IconButton>
              </ListItem>
              {index < videoTutorials.length - 1 && <Divider />}
            </React.Fragment>
          ))}
        </List>

        <Paper sx={{ p: 3, mt: 3, backgroundColor: 'info.light', color: 'info.contrastText' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <TutorialIcon sx={{ fontSize: 40 }} />
            <Box>
              <Typography variant="h6" sx={{ mb: 0.5 }}>
                Canal YouTube Oficial
              </Typography>
              <Typography variant="body2">
                Abonati-va la canalul nostru pentru mai multe tutoriale si actualizari
              </Typography>
              <Button
                variant="contained"
                size="small"
                sx={{ mt: 1 }}
                endIcon={<ExternalIcon />}
              >
                Viziteaza Canalul
              </Button>
            </Box>
          </Box>
        </Paper>
      </TabPanel>

      {/* Tab: Contact Suport */}
      <TabPanel value={tabValue} index={3}>
        <Grid container spacing={3}>
          {/* Contact Cards */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Card sx={{ height: '100%', textAlign: 'center', p: 3 }}>
              <EmailIcon color="primary" sx={{ fontSize: 48, mb: 2 }} />
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                Email
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Trimiteti-ne un email si va vom raspunde in max 24h
              </Typography>
              <Link href="mailto:support@healthypaws.ro" underline="none">
                <Button variant="contained" fullWidth>
                  support@healthypaws.ro
                </Button>
              </Link>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <Card sx={{ height: '100%', textAlign: 'center', p: 3 }}>
              <PhoneIcon color="success" sx={{ fontSize: 48, mb: 2 }} />
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                Telefon
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Luni - Vineri: 09:00 - 18:00
              </Typography>
              <Link href="tel:+40212345678" underline="none">
                <Button variant="contained" fullWidth>
                  +40 21 234 5678
                </Button>
              </Link>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <Card sx={{ height: '100%', textAlign: 'center', p: 3 }}>
              <WhatsAppIcon sx={{ fontSize: 48, mb: 2, color: '#25D366' }} />
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                WhatsApp
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Chat live pentru asistenta rapida
              </Typography>
              <Button variant="contained" fullWidth sx={{ backgroundColor: '#25D366', '&:hover': { backgroundColor: '#1fa855' } }}>
                Deschide Chat
              </Button>
            </Card>
          </Grid>

          {/* Formular Contact */}
          <Grid size={12}>
            <Card>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                  <FeedbackIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Trimite-ne un mesaj
                </Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Nume"
                      defaultValue={user?.name || ''}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Email"
                      type="email"
                      defaultValue={user?.email || ''}
                    />
                  </Grid>
                  <Grid size={12}>
                    <TextField
                      fullWidth
                      label="Subiect"
                      placeholder="Despre ce este mesajul?"
                    />
                  </Grid>
                  <Grid size={12}>
                    <TextField
                      fullWidth
                      label="Mesaj"
                      multiline
                      rows={6}
                      placeholder="Descrieti problema sau intrebarea dumneavoastra..."
                    />
                  </Grid>
                  <Grid size={12}>
                    <Button variant="contained" size="large" fullWidth>
                      Trimite Mesaj
                    </Button>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Link-uri utile */}
          <Grid size={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Link-uri Utile
                </Typography>
                <List>
                  <ListItemButton>
                    <ListItemIcon>
                      <BugIcon color="error" />
                    </ListItemIcon>
                    <ListItemText primary="Raporteaza un bug" secondary="Ajuta-ne sa imbunatatim platforma" />
                  </ListItemButton>
                  <Divider />
                  <ListItemButton>
                    <ListItemIcon>
                      <FeedbackIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Trimite feedback" secondary="Spune-ne parerile tale" />
                  </ListItemButton>
                  <Divider />
                  <ListItemButton>
                    <ListItemIcon>
                      <DocumentIcon color="info" />
                    </ListItemIcon>
                    <ListItemText primary="Documentatie API" secondary="Pentru dezvoltatori" />
                  </ListItemButton>
                  <Divider />
                  <ListItemButton>
                    <ListItemIcon>
                      <ArticleIcon color="warning" />
                    </ListItemIcon>
                    <ListItemText primary="Termeni si Conditii" secondary="Politica de utilizare" />
                  </ListItemButton>
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>
    </Box>
  );
};

export default Help;
